import type { CollaborationRole } from "./collaboration-crypto";
import { translate as t } from "./locale";
import type {
    AuthenticatedSession,
    Session,
    TeacherUser,
} from "./session";

export type CollaborationOwnerType = "student" | "teacher" | null;
export type CollaborationViewer = "owner" | "trainer";

export const hasTrainerAccess = (
    session: Session,
): session is AuthenticatedSession & { user: TeacherUser } =>
    session.authenticated && session.user.accountType === "teacher";

export const trainerPreflightFailure = (
    status: number,
): "invalid-link" | "login-required" =>
    status === 401 || status === 403 ? "login-required" : "invalid-link";

export const shouldAcceptPolledRevision = (
    knownRevision: number,
    candidateRevision: number,
): boolean => candidateRevision > knownRevision;

export const trainerSynchronizationStatus = ({
    authorRole,
    ownerAccountType,
    ownerActive,
    unavailable,
    updateAvailable,
}: {
    authorRole: CollaborationRole | null | undefined;
    ownerAccountType: CollaborationOwnerType | undefined;
    ownerActive: boolean | undefined;
    unavailable: boolean;
    updateAvailable: boolean;
}): { state: "loading" | "ready"; value: string } => {
    if (unavailable) return { state: "loading", value: t("niedostępna") };
    if (ownerActive === false)
        return { state: "loading", value: t("projekt ucznia nieaktywny") };
    if (!updateAvailable) return { state: "ready", value: t("aktualna") };
    if (authorRole === "trainer")
        return { state: "loading", value: t("nowe zmiany trenera") };
    if (ownerAccountType === "teacher")
        return { state: "loading", value: t("nowe zmiany właściciela") };
    return { state: "loading", value: t("nowe zmiany ucznia") };
};

export const collaborationChangeMessage = (
    viewer: CollaborationViewer,
    authorRole: CollaborationRole,
    ownerType: CollaborationOwnerType,
): string => {
    if (authorRole === "trainer")
        return t("Trener opublikował nowszą wersję projektu.");
    if (viewer === "trainer" && ownerType === "teacher")
        return t("Właściciel projektu opublikował nowszą wersję.");
    return t("Uczeń opublikował nowszą wersję projektu.");
};

export const nextOwnerPushState = (
    scheduledGeneration: number,
    cloudSaveGeneration: number,
    skipNext: boolean,
): {
    scheduledGeneration: number;
    shouldPush: boolean;
    skipNext: boolean;
} => {
    if (
        cloudSaveGeneration === 0 ||
        cloudSaveGeneration <= scheduledGeneration
    )
        return { scheduledGeneration, shouldPush: false, skipNext };
    return {
        scheduledGeneration: cloudSaveGeneration,
        shouldPush: !skipNext,
        skipNext: false,
    };
};
